import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface ParsedRow {
  row: number
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string
  valid: boolean
  errors: string[]
}

type Step = 'upload' | 'preview' | 'done'

const TEMPLATE_CSV = `เลขที่,คำนำหน้า,ชื่อ-นามสกุล,ชื่อเล่น
1,ด.ช.,กฤษณะ ใจดี,กฤษ
2,ด.ญ.,กัญญา มีสุข,กัน`

export function StudentImportPage() {
  const navigate = useNavigate()
  const fileRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step>('upload')
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [validCount, setValidCount] = useState(0)
  const [errorCount, setErrorCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  async function handleFile(file: File) {
    if (!file.name.endsWith('.csv')) {
      showToast('รองรับไฟล์ .csv เท่านั้น')
      return
    }

    setIsLoading(true)
    const csv = await file.text()

    try {
      const res = await fetch(apiUrl('/api/students/import/preview'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv }),
      })
      const data = await res.json()
      if (data.success) {
        setRows(data.data.rows)
        setValidCount(data.data.valid)
        setErrorCount(data.data.errors)
        setStep('preview')
      } else {
        showToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
      }
    } catch {
      showToast('เกิดข้อผิดพลาด')
    }
    setIsLoading(false)
  }

  async function handleConfirm() {
    setIsLoading(true)
    const validRows = rows.filter((r) => r.valid)

    try {
      const res = await fetch(apiUrl('/api/students/import/confirm'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: validRows }),
      })
      const data = await res.json()
      if (data.success) {
        showToast(data.message)
        setStep('done')
        setTimeout(() => navigate('/students'), 1500)
      } else {
        showToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
      }
    } catch {
      showToast('เกิดข้อผิดพลาด')
    }
    setIsLoading(false)
  }

  function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'template-students.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const steps = [
    { label: '1.อัปโหลด', active: step === 'upload' || step === 'preview' || step === 'done' },
    { label: '2.ตรวจสอบ', active: step === 'preview' || step === 'done' },
    { label: '3.ยืนยัน', active: step === 'done' },
  ]

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/students')} className="text-gray-600">⟨</button>
          <h1 className="text-lg font-bold text-gray-900">นำเข้ารายชื่อนักเรียน</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        {/* Step indicator */}
        <div className="mb-6 flex gap-2">
          {steps.map((s) => (
            <div key={s.label} className="flex-1">
              <div className={`h-1 rounded-full ${s.active ? 'bg-primary-500' : 'bg-gray-200'}`} />
              <p className={`mt-1 text-center text-xs ${s.active ? 'text-primary-600 font-medium' : 'text-gray-400'}`}>
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
              onDrop={(e) => {
                e.preventDefault()
                const file = e.dataTransfer.files[0]
                if (file) handleFile(file)
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white p-10 transition-colors hover:border-primary-400"
            >
              <span className="text-3xl">📄</span>
              <p className="mt-2 text-sm font-medium text-gray-700">วางไฟล์ CSV ที่นี่</p>
              <p className="text-xs text-gray-400">หรือ</p>
              <button className="mt-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white">
                เลือกไฟล์จากเครื่อง
              </button>
              <p className="mt-2 text-xs text-gray-400">รองรับ .csv เท่านั้น · ไม่เกิน 500 แถว</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
              }}
            />

            <div className="rounded-lg bg-white p-3 text-xs text-gray-500 shadow-sm">
              <p className="mb-1 font-medium">รูปแบบไฟล์ที่รองรับ:</p>
              <p className="font-mono">เลขที่, คำนำหน้า, ชื่อ, ชื่อเล่น</p>
              <p className="font-mono">1, ด.ช., กฤษณะ ใจดี, กฤษ</p>
            </div>

            <button onClick={downloadTemplate} className="text-sm text-primary-600 font-medium">
              ดาวน์โหลด template
            </button>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              พบ {rows.length} แถว · <span className="text-green-600">✓ {validCount} ถูกต้อง</span>
              {errorCount > 0 && <span className="text-red-600"> · ! {errorCount} ผิดพลาด</span>}
            </p>

            <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">คำนำหน้า</th>
                    <th className="px-3 py-2">ชื่อ-นามสกุล</th>
                    <th className="px-3 py-2">ชื่อเล่น</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.row}
                      className={r.valid ? '' : 'bg-red-50'}
                    >
                      <td className="px-3 py-2">{r.studentNumber}</td>
                      <td className="px-3 py-2">{r.prefix}</td>
                      <td className="px-3 py-2">{r.fullName}</td>
                      <td className="px-3 py-2">{r.nickname}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.valid ? (
                          <span className="text-green-500">✓</span>
                        ) : (
                          <span className="text-red-500" title={r.errors.join(', ')}>!</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              ข้อมูลที่มีอยู่จะถูกเก็บไว้ — ไม่มีการลบนักเรียนเดิม
            </div>

            <button
              onClick={handleConfirm}
              disabled={isLoading || validCount === 0}
              className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isLoading ? 'กำลังนำเข้า...' : `ยืนยันนำเข้า ${validCount} คน`}
            </button>
            <button
              onClick={() => { setStep('upload'); setRows([]) }}
              className="w-full py-2 text-sm text-gray-500"
            >
              ยกเลิก
            </button>
          </div>
        )}
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="students" />
    </div>
  )
}
